// Board document schema. Everything here is what lives inside the Yjs doc (see docs/02-architecture.md section 4).

export type ItemType =
  | 'image'
  | 'note'
  | 'title'
  | 'richtext'
  | 'table'
  | 'todo'
  | 'kanban'
  | 'shape'
  | 'stroke'
  | 'link'
  | 'file'
  | 'frame'
  | 'boardcard'
  | 'sheet';
export const ALL_ITEM_TYPES: ItemType[] = ['frame', 'image', 'note', 'title', 'richtext', 'table', 'todo', 'kanban', 'shape', 'stroke', 'link', 'file', 'boardcard', 'sheet'];
/** Types whose body is a Y.XmlFragment edited with TipTap. */
export const RICH_TYPES: ReadonlySet<ItemType> = new Set(['richtext', 'table', 'todo']);

/**
 * Ink tools. Pen, fineliner and brush draw lines (pressure pen, constant tip, tapered brush);
 * the highlighter draws translucent lines; fill paints the area inside the drawn loop.
 */
export type DrawTool = 'pen' | 'fineliner' | 'brush' | 'highlighter' | 'fill';
export const DRAW_TOOLS: DrawTool[] = ['pen', 'fineliner', 'brush', 'highlighter', 'fill'];
export const DRAW_TOOL_LABEL: Record<DrawTool, string> = {
  pen: 'Pen',
  fineliner: 'Fineliner',
  brush: 'Brush pen',
  highlighter: 'Highlighter',
  fill: 'Fill',
};

/**
 * Ink layers: the pen family (pen, fineliner, brush) shares one so its strokes stack in drawing
 * order; highlighter strokes and fills each get their own.
 */
export type InkKind = 'pen' | 'highlighter' | 'fill';
export const INK_KINDS: InkKind[] = ['pen', 'highlighter', 'fill'];
export const INK_LAYER_NAME: Record<InkKind, string> = { pen: 'Pen', highlighter: 'Highlighter', fill: 'Fill' };
/** Tolerates tool names from newer clients: anything unknown draws a line, so it belongs with the pens. */
export const inkKindOf = (tool: string): InkKind => (tool === 'highlighter' || tool === 'fill' ? tool : 'pen');
/** Id of the ink layer of a kind. Fixed so concurrent first strokes converge on one layer. */
export const inkLayerId = (kind: InkKind): string => `ink:${kind}`;

/** A layer (doc `layers` map). Items without a layer sit on the implicit bottom "Default" layer. */
export interface LayerDef {
  name: string;
  /** Higher order stacks on top. */
  order: number;
  visible: boolean;
  locked: boolean;
  /** Item types that land on this layer when created (manual assignment overrides). */
  autoTypes: ItemType[];
  /**
   * Ink layer: created automatically and holding every stroke of its kind. Strokes always live
   * on the ink layer for their tool, and are only selectable while the draw tool is active
   * (lasso), so sketching never clutters ordinary selection.
   */
  ink?: InkKind | null;
}

export const NOTE_COLORS = ['yellow', 'pink', 'green', 'blue', 'orange', 'gray'] as const;
export type NoteColor = (typeof NOTE_COLORS)[number];

export type ShapeKind = 'rect' | 'ellipse' | 'line' | 'arrow';
export type TextAlign = 'left' | 'center' | 'right';
/** Where a note's text sits in its box. */
export type VerticalAlign = 'top' | 'middle' | 'bottom';

export interface ItemBase {
  id: string;
  type: ItemType;
  /** Top-left of the unrotated box, world units. Rotation is about the box centre. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Degrees, clockwise. */
  rotation: number;
  /** Fractional index; items sort ascending, last is on top. */
  z: string;
  layerId: string | null;
  groupId: string | null;
  locked: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface ImageItem extends ItemBase {
  type: 'image';
  props: { assetId: string; naturalW: number; naturalH: number; opacity: number };
}

export interface NoteItem extends ItemBase {
  type: 'note';
  props: {
    text: string;
    /** Paper colour. */
    color: NoteColor;
    fontSize: number;
    font?: string | null;
    textColor?: string | null;
    bold?: boolean;
    italic?: boolean;
    align?: TextAlign;
    valign?: VerticalAlign | null;
  };
}

export interface TitleItem extends ItemBase {
  type: 'title';
  props: {
    text: string;
    fontSize: number;
    bold: boolean;
    align: TextAlign;
    italic?: boolean;
    color?: string | null;
    font?: string | null;
  };
}

/**
 * Rich text. The document body is a Y.XmlFragment stored under props.content in the Yjs doc
 * (created by BoardDoc.createItems); the read index only sees its XML string.
 */
export interface RichProps {
  bg: string | null;
  fontSize: number;
  /** XML string view of the fragment (read index only). */
  content?: string;
  /** ProseMirror JSON used when creating a copy (clipboard/duplicate/starter content). */
  contentJson?: unknown;
  color?: string | null;
  font?: string | null;
  align?: TextAlign;
}

export interface RichTextItem extends ItemBase {
  type: 'richtext';
  props: RichProps;
}

export interface TableItem extends ItemBase {
  type: 'table';
  props: RichProps;
}

export interface TodoItem extends ItemBase {
  type: 'todo';
  props: RichProps;
}

export interface KanbanCard {
  id: string;
  text: string;
}

export interface KanbanColumn {
  id: string;
  title: string;
  cards: KanbanCard[];
}

export interface KanbanItem extends ItemBase {
  type: 'kanban';
  props: { columns: KanbanColumn[] };
}

export interface FileItem extends ItemBase {
  type: 'file';
  props: { assetId: string; name: string; size: number; mime: string };
}

export interface ShapeItem extends ItemBase {
  type: 'shape';
  props: {
    kind: ShapeKind;
    /** CSS colour or 'none'. */
    fill: string;
    stroke: string;
    strokeWidth: number;
    /** Lines/arrows run top-left to bottom-right unless flipped (bottom-left to top-right). */
    flip: boolean;
  };
}

export interface StrokeItem extends ItemBase {
  type: 'stroke';
  props: {
    tool: DrawTool;
    color: string;
    /** Brush size in world units at drawing time (unused by fills). */
    size: number;
    /** Flat [x, y, pressure, ...] in the item's own coordinate space (0..w0, 0..h0). Fills: the outline. */
    points: number[];
    w0: number;
    h0: number;
    /** The points carry real pressure (stylus, straight lines); otherwise the pen simulates it. */
    pressure: boolean;
    /** 0..1; missing means opaque. */
    opacity?: number;
    /** Ends made by the eraser (1 = start, 2 = end, 3 = both): a brush leaves them blunt, not tapered. */
    cut?: number;
    /**
     * Fills only: paths rubbed out by the eraser, each [radius, x, y, x, y, ...] in the item's own
     * space. Fills are masked by them rather than cut apart.
     */
    erased?: number[][];
  };
}

export interface LinkItem extends ItemBase {
  type: 'link';
  props: {
    url: string;
    title: string;
    description: string;
    /** Preview picture on the linked site. */
    image: string | null;
    /**
     * The preview picture kept on this board instead, for sites whose image links expire
     * (Instagram). Shown in place of `image`; missing on older cards.
     */
    imageAssetId?: string | null;
    site: string;
    status: 'loading' | 'ok' | 'error';
  };
}

/**
 * A frame: a labelled rectangle that acts as a container. Membership is by position (an item
 * whose centre lies inside the frame belongs to it), so moving a frame carries its contents.
 * Frames are created at the bottom of the stack.
 */
export interface FrameItem extends ItemBase {
  type: 'frame';
  props: {
    title: string;
    /** Border/label colour, or null for the theme line colour. */
    color: string | null;
  };
}

export type BoardCardLayout = 'card' | 'poster';
export type BoardCardFit = 'cover' | 'contain';

/**
 * A card that embeds (links to) another board on the same server. Double-click opens it. The
 * board's title and dashboard preview are looked up live; a custom title, description and
 * cover image (an asset uploaded to *this* board) can replace them.
 */
export interface BoardCardItem extends ItemBase {
  type: 'boardcard';
  props: {
    boardId: string;
    /** Shown instead of the board's own title when not empty. */
    title: string;
    description: string;
    /** Cover image (asset on this board), or null to show the board's live preview. */
    coverAssetId: string | null;
    /** Without a custom cover: show the board's dashboard preview (true) or a blank cover (false). */
    showPreview: boolean;
    /** 'card' stacks the cover above the text; 'poster' fills the card with the cover and overlays the text. */
    layout: BoardCardLayout;
    fit: BoardCardFit;
    /** Accent for the border and label, or null for the theme line colour. */
    color: string | null;
  };
}

/** One layer of a sketch sheet (props.layers map). Higher order stacks on top. */
export interface SheetLayerDef {
  name: string;
  order: number;
  visible: boolean;
  locked: boolean;
  /** 0..1 */
  opacity: number;
  /** PNG asset of exactly pxW × pxH pixels, or null for an empty layer. */
  assetId: string | null;
}

/**
 * A sketch sheet: a raster painting surface with its own layer stack. The pixel size is fixed
 * at creation; the box on the board (w, h) only scales the display, like an image. Layers are
 * nested Y.Maps in the document (created by BoardDoc.createItems) so a paint commit and a rename
 * from two devices merge; the read index sees them as plain objects.
 */
export interface SheetItem extends ItemBase {
  type: 'sheet';
  props: {
    pxW: number;
    pxH: number;
    /** Nominal resolution; sets the default board size (pixels / dpi × 96). */
    dpi: number;
    /** Preset id (see board/paint/presets.ts) or 'custom'. */
    format: string;
    /** Paper colour, or null for a transparent sheet. */
    paper: string | null;
    layers: Record<string, SheetLayerDef>;
  };
}

export type Item =
  | FrameItem
  | ImageItem
  | NoteItem
  | TitleItem
  | RichTextItem
  | TableItem
  | TodoItem
  | KanbanItem
  | ShapeItem
  | StrokeItem
  | LinkItem
  | FileItem
  | BoardCardItem
  | SheetItem;

type OptionalOnCreate =
  | 'id'
  | 'z'
  | 'rotation'
  | 'layerId'
  | 'groupId'
  | 'locked'
  | 'createdAt'
  | 'updatedAt';

/** Input for creating an item: the id, z-order and bookkeeping fields are filled in by BoardDoc. */
export type NewItem = {
  [I in Item as I['type']]: Omit<I, OptionalOnCreate> & Partial<Pick<I, OptionalOnCreate>>;
}[ItemType];

/** Partial update. `props` keys are merged into the existing props map. */
export interface ItemPatch {
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  rotation?: number;
  z?: string;
  layerId?: string | null;
  groupId?: string | null;
  locked?: boolean;
  props?: Record<string, unknown>;
}

export interface Geometry {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

export const DEFAULT_NOTE_SIZE = 200;
export const DEFAULT_TITLE_SIZE = { w: 480, h: 64 };
export const DEFAULT_RICHTEXT_SIZE = { w: 360, h: 200 };
export const DEFAULT_SHAPE_SIZE = 160;
export const DEFAULT_LINK_SIZE = { w: 300, h: 260 };
export const DEFAULT_TABLE_SIZE = { w: 480, h: 160 };
export const DEFAULT_TODO_SIZE = { w: 280, h: 180 };
export const DEFAULT_KANBAN_SIZE = { w: 720, h: 420 };
export const DEFAULT_FILE_SIZE = { w: 240, h: 64 };
export const DEFAULT_BOARDCARD_SIZE = { w: 320, h: 240 };
export const MIN_ITEM_SIZE = 8;
/** Sketch sheets: pixel limits (a 600 dpi A4 is 35 Mpx). */
export const SHEET_MAX_SIDE = 16000;
export const SHEET_MAX_PIXELS = 40_000_000;
export const SHEET_WARN_PIXELS = 20_000_000;
export const SHEET_MIN_SIDE = 16;

/** Item types whose content is edited inline on double-click. */
export const EDITABLE_TYPES: ReadonlySet<ItemType> = new Set(['note', 'title', 'richtext', 'table', 'todo', 'kanban', 'frame']);
